import React from "react";

import type {
  ListingInferenceReviewPortalClaimRow,
  ListingInferenceReviewPortalPhase,
  ListingInferenceReviewPortalProvenanceRow,
  ListingInferenceReviewPortalStatusMessage,
  ListingInferenceReviewPortalWorkflowProps,
} from "@view/listing/inference/reviewPortal/ListingInferenceReviewPortalPhase";
import ListingInferenceReviewPortalUnavailable from "@view/listing/inference/reviewPortal/ListingInferenceReviewPortalUnavailable";
import ListingInferenceReviewPortalWorkflow from "@view/listing/inference/reviewPortal/ListingInferenceReviewPortalWorkflow";
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";

type ListingInferenceReviewPortalComputedWorkflowProps =
  | "acceptedDecisionMessages"
  | "decisionTargetMessage"
  | "errorMessages"
  | "provenanceEmptyMessage"
  | "provenanceRows"
  | "selectedClaimContextMessage";

type ListingInferenceReviewPortalPanelProps = Omit<
  ListingInferenceReviewPortalWorkflowProps,
  ListingInferenceReviewPortalComputedWorkflowProps
>;

const errorMessagesFor = (errorMessage: string): readonly ListingInferenceReviewPortalStatusMessage[] =>
  errorMessage.length > 0 ? [{ id: "route-error", message: errorMessage }] : [];

const acceptedDecisionMessagesFor = (acceptedDecision: string): readonly ListingInferenceReviewPortalStatusMessage[] =>
  acceptedDecision.length > 0 ? [{ id: "accepted-decision", message: acceptedDecision }] : [];

const provenanceRowsFor = (
  selectedClaim: OptionTs<ListingInferenceReviewPortalClaimRow>,
): readonly ListingInferenceReviewPortalProvenanceRow[] =>
  Option.fold(selectedClaim, () => [], (claim: ListingInferenceReviewPortalClaimRow) => [
    { label: "Source", value: claim.sourceRef },
    { label: "Speaker", value: claim.speaker },
    { label: "Observed", value: claim.observedAt },
  ]);

const provenanceEmptyMessageFor = (selectedClaim: OptionTs<ListingInferenceReviewPortalClaimRow>): string =>
  Option.fold(selectedClaim, () => "No claim selected.", () => "");

const unavailableMessageFor = (phase: ListingInferenceReviewPortalPhase): string =>
  phase === "readDenied" ? "Read permission denied." : "Review portal unavailable.";

const decisionTargetMessageFor = (selectedClaimLabel: string): string =>
  selectedClaimLabel.length > 0
    ? `Decision target: ${selectedClaimLabel}`
    : "Decision target: Select a claim before submitting.";

const selectedClaimContextMessageFor = (provenanceEmptyMessage: string, selectedClaimLabel: string): string =>
  provenanceEmptyMessage.length > 0 ? provenanceEmptyMessage : `Selected claim context: ${selectedClaimLabel}`;

const ListingInferenceReviewPortalPanel: React.FC<ListingInferenceReviewPortalPanelProps> = ({
  acceptedDecision,
  errorMessage,
  gaps,
  hasProvider,
  hasProgramBoundary,
  includeGaps,
  listingId,
  onRetryLoad,
  onRetrySubmit,
  phase,
  selectedClaim,
  selectedClaimLabel,
  ...workflowProps
}) => {
  const retryHandler = phase === "submitFailed" ? onRetrySubmit : onRetryLoad;
  const visibleGaps = includeGaps ? gaps : [];
  const provenanceEmptyMessage = provenanceEmptyMessageFor(selectedClaim);
  const unavailableView = (
    <ListingInferenceReviewPortalUnavailable
      hasProvider={hasProvider}
      hasProgramBoundary={hasProgramBoundary}
      listingId={listingId}
      phase={phase}
      statusMessages={errorMessagesFor(errorMessage)}
      unavailableMessage={unavailableMessageFor(phase)}
    />
  );
  const workflowView = (
    <ListingInferenceReviewPortalWorkflow
      hasProvider={hasProvider}
      hasProgramBoundary={hasProgramBoundary}
      listingId={listingId}
      acceptedDecision={acceptedDecision}
      acceptedDecisionMessages={acceptedDecisionMessagesFor(acceptedDecision)}
      errorMessage={errorMessage}
      errorMessages={errorMessagesFor(errorMessage)}
      gaps={visibleGaps}
      includeGaps={includeGaps}
      onRetryLoad={retryHandler}
      onRetrySubmit={onRetrySubmit}
      phase={phase}
      decisionTargetMessage={decisionTargetMessageFor(selectedClaimLabel)}
      provenanceEmptyMessage={provenanceEmptyMessage}
      provenanceRows={provenanceRowsFor(selectedClaim)}
      selectedClaim={selectedClaim}
      selectedClaimContextMessage={selectedClaimContextMessageFor(provenanceEmptyMessage, selectedClaimLabel)}
      selectedClaimLabel={selectedClaimLabel}
      {...workflowProps}
    />
  );
  const viewByPhase = {
    featureUnavailable: unavailableView,
    queueEmpty: workflowView,
    noListingSelected: workflowView,
    loading: workflowView,
    loadedWorkspace: workflowView,
    noSelectedClaim: workflowView,
    readDenied: unavailableView,
    loadFailed: workflowView,
    writeDeniedReadOnly: workflowView,
    submittingDecision: workflowView,
    submitFailed: workflowView,
    submittedRefreshing: workflowView,
    summaryUpdated: workflowView,
    unsafeFactPromotionRefused: workflowView,
  } satisfies Record<ListingInferenceReviewPortalPhase, React.ReactElement>;

  return viewByPhase[phase];
};

export default ListingInferenceReviewPortalPanel;
